import React, { useEffect, useState } from "react";
import IntlCurrencyInput from "react-intl-currency-input";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import driverService from "../../../service/driver.service";
import theme from "../../../theme";
import { cardIconSx } from "../../../theme/globalStyle/globalStyle";
import { resolveVehicleForDriver } from "../../../util/vehicle/resolveVehicleForDriver";

const currencyConfig = {
  locale: "pt-BR",
  formats: {
    number: {
      BRL: {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
  },
};

function formatCPF(cpf) {
  if (!cpf) return "";
  const digits = String(cpf).replace(/\D/g, "");
  if (digits.length !== 11) return String(cpf);
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

function isSeparatorOrPlaceholder(driver) {
  if (!driver || typeof driver !== "object") return true;
  if (driver.__separator === true) return true;
  const name = driver.name || "";
  if (name === "Não aplicar motorista" || name === "Nao aplicar motorista") {
    return true;
  }
  if (name.includes("---")) return true;
  return false;
}

function getDriverLabel(option, formatName) {
  if (!option || typeof option !== "object") return "";
  if (option.__separator === true) return "";
  return formatName(option.name || "");
}

function isSameDriver(option, value) {
  if (!option || !value) return false;
  if (typeof option !== "object" || typeof value !== "object") return false;

  const optionKey = option.id || option.uid || option.value || option.name;
  const valueKey = value.id || value.uid || value.value || value.name;

  return optionKey === valueKey;
}

function resolveDriverDocId(driverChose, uidDriver) {
  if (driverChose && typeof driverChose === "object") {
    const id = driverChose.id || driverChose.uid;
    if (id) return id;
  }
  if (uidDriver) return uidDriver;
  return "";
}

function normalizePlate(plate) {
  if (!plate) return "";
  return String(plate).replace(/[^a-zA-Z0-9]/g, "");
}

function buildPlacasLine(vehicle) {
  if (!vehicle || typeof vehicle !== "object") return "";
  const parts = [];
  if (vehicle.vehiclePlate) parts.push(`Cavalo: ${normalizePlate(vehicle.vehiclePlate)}`);
  if (vehicle.bodyworkPlate) parts.push(`Carroceria: ${normalizePlate(vehicle.bodyworkPlate)}`);
  if (Array.isArray(vehicle.reboques) && vehicle.reboques.length > 0) {
    const plates = vehicle.reboques
      .map((r) => normalizePlate(r.placaReboque))
      .filter(Boolean);
    if (plates.length) parts.push(`Reboque(s): ${plates.join(", ")}`);
  }
  return parts.join(" | ");
}

function buildVehicleDescription(vehicle) {
  if (!vehicle || typeof vehicle !== "object") return "";
  return [vehicle.vehicleType, vehicle.bodyworkType, vehicle.descricaoVeiculo]
    .filter(Boolean)
    .join(" - ");
}

function ReadonlyField({ label, value, loading }) {
  return (
    <Box sx={fieldWrapSx}>
      <Typography component="span" sx={fieldLabelSx}>
        {label}
      </Typography>
      {loading ? (
        <Box sx={readonlyLoadingSx}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          size="small"
          value={value || "-"}
          InputProps={{ readOnly: true }}
          sx={readonlyFieldSx}
        />
      )}
    </Box>
  );
}

function DriverOption({ props, option, formatName }) {
  const { key, ...liProps } = props;
  const driverName = formatName(option.name || "");

  return (
    <Box
      component="li"
      key={option.id || option.name || key}
      {...liProps}
      sx={routeOptionItemSx}
    >
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography
          className="route-option-primary"
          noWrap
          sx={routeOptionPrimarySx}
        >
          {driverName}
        </Typography>
        {option.cpf ? (
          <Typography noWrap sx={routeOptionSecondarySx}>
            CPF: {formatCPF(option.cpf)}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

function RequiredMark({ show }) {
  if (!show) return null;
  return (
    <>
      {" "}
      <span style={{ color: "red" }}>*</span>
    </>
  );
}

function FreightDriverNegotiationCard({
  priceANTT,
  view,
  user,
  verifyPayment,
  valueDriverInitial,
  valueDriverFinal,
  valueNegotiated,
  paymentCondition,
  driverChose,
  driversInFila,
  uidDriver,
  transportOptions = [],
  transportValue,
  onTransportSelect,
  getTransportOptionLabel,
  filterTransportOptions,
  requireTransport = false,
  /** Frete já aceito/atribuído: transportadora não altera o campo. */
  transportDisabled = false,
  formatName,
  onDriverInitialChange,
  onDriverFinalChange,
  onNegotiatedChange,
  onPaymentConditionChange,
  onDriverSelect,
  requireValueDriverInitial = false,
  requireValueDriverFinal = false,
  requireValueNegotiated = false,
  requirePaymentCondition = false,
  hideSuggestedAndAnnouncedValues = false,
  /** Esconde negociado + condição de pagamento (ownership TRANSPORTADORA p/ embarcador). */
  hideNegotiatedAndPaymentCondition = false,
}) {
  const hideSuggestedAndAnnounced =
    hideSuggestedAndAnnouncedValues || hideNegotiatedAndPaymentCondition;
  const hideNegotiatedAndPayment = hideNegotiatedAndPaymentCondition;
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [complementary, setComplementary] = useState({
    cpf: "",
    plates: "",
    vehicle: "",
  });

  

  useEffect(() => {
    let cancelled = false;

    async function loadComplementary() {
      const docId = resolveDriverDocId(driverChose, uidDriver);
      const skip =
        !docId ||
        (driverChose &&
          typeof driverChose === "object" &&
          isSeparatorOrPlaceholder(driverChose));

      if (skip) {
        setComplementary({ cpf: "", plates: "", vehicle: "" });
        setLoadingDetail(false);
        return;
      }

      setLoadingDetail(true);
      try {
        const snap = await driverService.getDriverAvailableById(docId);
        if (cancelled) return;

        if (!snap.exists) {
          const cpfFallback =
            driverChose && typeof driverChose === "object" && driverChose.cpf
              ? driverChose.cpf
              : "";
          setComplementary({
            cpf: cpfFallback ? formatCPF(cpfFallback) : "",
            plates: "",
            vehicle: "",
          });
          setLoadingDetail(false);
          return;
        }

        const allData = snap.data() || {};
        const personal = allData.personalData || {};
        const vehicle =
          (await resolveVehicleForDriver(docId, allData)) || {};

        const cpfRaw =
          personal.documentCpf ||
          (driverChose && typeof driverChose === "object" && driverChose.cpf) ||
          "";

        setComplementary({
          cpf: cpfRaw ? formatCPF(cpfRaw) : "",
          plates: buildPlacasLine(vehicle),
          vehicle: buildVehicleDescription(vehicle),
        });
      } catch (e) {
        if (!cancelled) {
          console.error(
            "FreightDriverNegotiationCard: erro ao carregar motorista",
            e,
          );
          setComplementary({ cpf: "", plates: "", vehicle: "" });
        }
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    }

    loadComplementary();
    return () => {
      cancelled = true;
    };
  }, [driverChose, uidDriver]);

  const cpfFromChose =
    driverChose &&
    typeof driverChose === "object" &&
    driverChose.cpf &&
    !isSeparatorOrPlaceholder(driverChose)
      ? formatCPF(driverChose.cpf)
      : "";

  const displayCpf = complementary.cpf || cpfFromChose || "";
  const selectedDriver =
    driverChose &&
    typeof driverChose === "object" &&
    !isSeparatorOrPlaceholder(driverChose)
      ? driverChose
      : null;
  const driverOptions = [
    {
      name: "Não aplicar motorista",
      cpf: "",
      value: "",
      id: "",
      uid: "",
    },
    ...driversInFila,
  ];

  const canViewNegotiation = user?.permissoes?.freightDriver?.isView;
  const canEditDriver = user?.permissoes?.freightDriver?.isEdit;
  const isDriverDisabled = view || !canEditDriver;
  const isNegotiationDisabled = verifyPayment || view || !canEditDriver;

  return (
    <Card className="mb-3 form-signin mx-auto mb-4 mb-lg-6" sx={cardSx}>
      <CardContent
        sx={{
          p: 2,
          "&:last-child": { pb: 2 },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 3 }}>
          <Box sx={cardIconSx}>
            <HandshakeOutlinedIcon fontSize="small" />
          </Box>

          <Typography sx={cardTitleSx}>
            {canViewNegotiation ? "Motorista e Negociação" : "Motorista"}
          </Typography>
        </Stack>

        <Grid
          container
          spacing={2}
          alignItems="stretch"
        >
          <Grid item xs={12} md={4} lg={3}>
            <Box sx={fieldWrapSx}>
              <Typography component="label" htmlFor="chose-1" sx={fieldLabelSx}>
                Motorista
              </Typography>
              <Autocomplete
                disabled={isDriverDisabled}
                id="chose-1"
                size="small"
                value={selectedDriver}
                getOptionLabel={(option) => getDriverLabel(option, formatName)}
                options={driverOptions}
                isOptionEqualToValue={isSameDriver}
                getOptionDisabled={(option) => option.__separator === true}
                onChange={(event, value) => {
                  onDriverSelect(isSeparatorOrPlaceholder(value) ? null : value);
                }}
                clearOnBlur
                noOptionsText="Nenhum motorista encontrado"
                slotProps={routeAutocompleteSlotProps}
                sx={selectSx}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Nome ou CPF" />
                )}
                renderOption={(props, option) => {
                  if (option.__separator === true) {
                    const { key, ...rest } = props;
                    return (
                      <Box
                        key={option.id || key}
                        component="li"
                        {...rest}
                        sx={separatorOptionSx}
                      >
                        <Box sx={separatorLineSx} />
                      </Box>
                    );
                  }
                  return (
                    <DriverOption
                      props={props}
                      option={option}
                      formatName={formatName}
                    />
                  );
                }}
                filterOptions={(options, { inputValue }) => {
                  const normalizedInput = inputValue.trim();
                  const normalizedCPFInput = normalizedInput.replace(/\D/g, "");
                  const isNumber =
                    normalizedCPFInput !== "" && !isNaN(normalizedCPFInput);

                  return options.filter((option) => {
                    if (option.__separator === true) {
                      return normalizedInput === "";
                    }
                    if (isNumber) {
                      return (option.cpf || "").includes(normalizedCPFInput);
                    }
                    return option.name
                      ?.toLowerCase()
                      .includes(normalizedInput.toLowerCase());
                  });
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
              <Grid item xs={12} sm={3}>
                <ReadonlyField
                  label="CPF"
                  value={displayCpf}
                  loading={loadingDetail}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <ReadonlyField
                  label="Placas (cavalo, carroceria e reboques)"
                  value={complementary.plates}
                  loading={loadingDetail}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ReadonlyField
                  label="Veiculo / Carroceria"
                  value={complementary.vehicle}
                  loading={loadingDetail}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Box sx={fieldWrapSx}>
              <Typography
                component="label"
                htmlFor="transportadora-select"
                sx={fieldLabelSx}
              >
                Transportadora
                {requireTransport ? (
                  <Box component="span" sx={requiredSx}>
                    {" "}
                    *
                  </Box>
                ) : null}
              </Typography>
              <Autocomplete
                disabled={view || transportDisabled}
                id="transportadora-select"
                size="small"
                value={transportValue ?? null}
                options={transportOptions}
                getOptionLabel={(option) =>
                  typeof getTransportOptionLabel === "function"
                    ? getTransportOptionLabel(option)
                    : option?.name || ""
                }
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                filterOptions={filterTransportOptions}
                onChange={(event, value) => {
                  onTransportSelect?.(value);
                }}
                noOptionsText="Nenhuma transportadora encontrada"
                slotProps={routeAutocompleteSlotProps}
                sx={selectSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione a transportadora"
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={option?.id || key} {...optionProps}>
                      {typeof getTransportOptionLabel === "function"
                        ? getTransportOptionLabel(option)
                        : option?.name || ""}
                    </li>
                  );
                }}
              />
            </Box>
          </Grid>

          {canViewNegotiation ? (
            <>
              {!hideNegotiatedAndPayment ? (
                <>
                  <Grid item xs={12} sm={6} lg={3}>
                    <Box sx={fieldWrapSx}>
                      <Typography
                        component="label"
                        htmlFor="valueNegotiated"
                        sx={fieldLabelSx}
                      >
                        Valor Negociado <Box component="span" sx={requiredSx}>*</Box>
                      </Typography>
                      <IntlCurrencyInput
                        currency="BRL"
                        config={currencyConfig}
                        placeholder="00.00"
                        onChange={onNegotiatedChange}
                        value={valueNegotiated ?? ""}
                        className="form-control"
                        id="valueNegotiated"
                        disabled={isNegotiationDisabled}
                        style={currencyInputStyle}
                      />

                      {verifyPayment ? (
                        <Typography sx={helperErrorSx}>
                          O status do lançamento financeiro não permite alteração.
                        </Typography>
                      ) : null}
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} lg={3}>
                    <Box sx={fieldWrapSx}>
                      <Typography
                        component="label"
                        htmlFor="paymentCondition"
                        sx={fieldLabelSx}
                      >
                        Condições de Pagamento
                        {requirePaymentCondition ? (
                          <Box component="span" sx={requiredSx}>
                            *
                          </Box>
                        ) : null}
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        id="paymentCondition"
                        value={paymentCondition ?? ""}
                        onChange={onPaymentConditionChange}
                        disabled={isNegotiationDisabled}
                        sx={selectSx}
                      >
                        <MenuItem value="">Selecione</MenuItem>
                        <MenuItem value="70/30">70%/30%</MenuItem>
                        <MenuItem value="80/20">80%/20%</MenuItem>
                        <MenuItem value="50/50">50%/50%</MenuItem>
                        <MenuItem value="0/100">0%/100%</MenuItem>
                      </TextField>
                    </Box>
                  </Grid>
                </>
              ) : null}

              {!hideSuggestedAndAnnounced ? (
                <>
                  {<Grid item xs={12} sm={6} lg={3}>
                    <Box sx={fieldWrapSx}>
                      <Typography
                        component="label"
                        htmlFor="valueDriverInitial"
                        sx={fieldLabelSx}
                      >
                        Valor Sugerido (ANTT + Pedágio)
                      </Typography>
                      <IntlCurrencyInput
                      disabled={view   || !canEditDriver || !priceANTT == 0 || !priceANTT == ''}
                        
                        currency="BRL"
                        config={currencyConfig}
                        placeholder="00.00"
                        onChange={onDriverInitialChange}
                        value={valueDriverInitial ?? ""}
                        className="form-control"
                        id="valueDriverInitial"
                        maxLength={12}
                        style={currencyInputStyle}
                      />
                    </Box>
                  </Grid>}

                  <Grid item xs={12} sm={6} lg={3}>
                    <Box sx={fieldWrapSx}>
                      <Typography
                        component="label"
                        htmlFor="valueDriverFinal"
                        sx={fieldLabelSx}
                      >
                        Valor Anunciado
                      </Typography>
                      <IntlCurrencyInput
                        disabled={isDriverDisabled}
                        currency="BRL"
                        config={currencyConfig}
                        placeholder="00.00"
                        onChange={onDriverFinalChange}
                        value={valueDriverFinal ?? ""}
                        className="form-control"
                        id="valueDriverFinal"
                        maxLength={12}
                        style={currencyInputStyle}
                      />
                    </Box>
                  </Grid>
                </>
              ) : null}
            </>
          ) : null}
        </Grid>
      </CardContent>

      <div className="row gap-4 align-items-center gx-3">
        {!hideNegotiatedAndPayment ? (
          <>
            <div className="col-2" style={hiddenFieldStyle}>
            <label
              htmlFor="valueNegotiated"
              placeholder="0,00"
              className="font-test"
            >
              Valor Negociado:
              <RequiredMark show={requireValueNegotiated} />
            </label>
            <IntlCurrencyInput
              currency="BRL"
              config={currencyConfig}
              placeholder="00.00"
              onChange={onNegotiatedChange}
              value={valueNegotiated && valueNegotiated}
              className="form-control"
              id="valueNegotiated"
              disabled={verifyPayment || view || !user?.permissoes?.freightDriver?.isEdit}
            />

            {verifyPayment && (
              <small className="text-danger">
                O status do lançamento financeiro não permite alteração!
              </small>
            )}
          </div>
          <div className="col-2" style={hiddenFieldStyle}>
            <label
              htmlFor="paymentCondition"
              className="font-test"
              placeholder="0,00"
              style={{ whiteSpace: "nowrap" }}
            >
              Condições de Pagamento:
            <RequiredMark show={requirePaymentCondition} />
            </label>
            <select
              disabled={verifyPayment || view || !user?.permissoes?.freightDriver?.isEdit}
              value={paymentCondition && paymentCondition}
              id="paymentCondition"
              className="form-select"
              onChange={onPaymentConditionChange}
            >
              <option value="">Selecione</option>
              <option value="70/30">70%/30%</option>
              <option value="80/20">80%/20%</option>
              <option value="50/50">50%/50%</option>
              <option value="0/100">0%/100%</option>
            </select>
          </div>
          </>
        ) : null}
          {!hideSuggestedAndAnnounced ? (
            <>
              <div className="col-2" style={hiddenFieldStyle}>
                <label htmlFor="valueDriverInitial" className="font-test">
                  Valor Sugerido:
                  <RequiredMark show={requireValueDriverInitial} />
                </label>
                <IntlCurrencyInput
                  disabled={
                    view ||
                    transportDisabled ||
                    !user?.permissoes?.freightDriver?.isEdit
                  }
                  currency="BRL"
                  config={currencyConfig}
                  placeholder="00.00"
                  onChange={onDriverInitialChange}
                  value={valueDriverInitial && valueDriverInitial}
                  className="form-control"
                  id="valueDriverInitial"
                  maxLength={12}
                />
              </div>

              <div className="col-2" style={hiddenFieldStyle}>
                <label
                  htmlFor="valueDriverFinal"
                  placeholder="0,00"
                  className="font-test"
                >
                  Valor Anunciado:
                  <RequiredMark show={requireValueDriverFinal} />
                </label>
                <IntlCurrencyInput
                  disabled={view || !user?.permissoes?.freightDriver?.isEdit}
                  currency="BRL"
                  config={currencyConfig}
                  placeholder="00.00"
                  onChange={onDriverFinalChange}
                  value={valueDriverFinal && valueDriverFinal}
                  className="form-control"
                  id="valueDriverFinal"
                  maxLength={12}
                />
              </div>
            </>
          ) : null}
        </div>
    </Card>
  );
}

const cardSx = {
  width: "100%",
  display: "block",
  boxSizing: "border-box",
  overflow: "visible",
  mx: "auto",
  borderRadius: `${theme.radius.large}px`,
  background: theme.colors.surface,
  boxShadow:
    "0px 4px 10px rgba(0,14,51,0.16), 0px 3px 6px rgba(0,14,51,0.12)",
  border: "0px solid rgba(1, 36, 66, 0.28)",
};

const cardTitleSx = {
  color: theme.colors.primary,
  fontWeight: theme.typography.weightBold,
  fontSize: "1.25rem",
};

const fieldWrapSx = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minWidth: 0,
};

const fieldLabelSx = {
  display: "block",
  mb: 1,
  color: theme.colors.textPrimary,
  fontSize: theme.typography.bodySize,
  fontWeight: theme.typography.weightBold,
  lineHeight: 1.35,
  maxWidth: "100%",
  wordBreak: "break-word",
  overflowWrap: "break-word",
};

const selectSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: `${theme.radius.small}px`,
    fontSize: 15,
    minHeight: 40,
    backgroundColor: theme.colors.surface,
  },
  "& .MuiInputBase-input": {
    fontSize: 15,
  },
};

const readonlyFieldSx = {
  flex: 1,
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    borderRadius: `${theme.radius.small}px`,
    backgroundColor: "rgba(1, 36, 66, 0.03)",
  },
  "& .MuiInputBase-input": {
    color: theme.colors.textPrimary,
    cursor: "default",
    fontSize: 14,
    overflowWrap: "break-word",
    wordBreak: "break-word",
  },
};

const readonlyLoadingSx = {
  minHeight: 40,
  display: "flex",
  alignItems: "center",
  px: 1.5,
  borderRadius: `${theme.radius.small}px`,
  border: "1px solid rgba(1, 36, 66, 0.16)",
  background: "rgba(1, 36, 66, 0.03)",
};

const currencyInputStyle = {
  width: "100%",
  height: 40,
  borderRadius: `${theme.radius.small}px`,
  border: "1px solid rgba(1, 36, 66, 0.22)",
  color: theme.colors.textPrimary,
  fontSize: 15,
  padding: "8px 12px",
  outline: "none",
  boxShadow: "none",
};

const hiddenFieldStyle = {
  display: "none",
};

const requiredSx = {
  color: theme.colors.highlight,
  fontWeight: theme.typography.weightBold,
};

const helperErrorSx = {
  mt: 0.75,
  color: theme.colors.highlight,
  fontSize: theme.typography.captionSize,
  fontWeight: theme.typography.weightSemibold,
};

const routeAutocompleteSlotProps = {
  paper: {
    sx: {
      mt: 1,
      borderRadius: "16px",
      border: "1px solid rgba(1, 36, 66, 0.16)",
      boxShadow: "0 14px 28px rgba(1, 36, 66, 0.18)",
      overflow: "hidden",
    },
  },
  listbox: {
    sx: {
      p: 0.75,
      maxHeight: 360,
      "& .MuiAutocomplete-option": {
        p: 0,
      },
    },
  },
};

const routeOptionItemSx = {
  borderRadius: "12px",
  px: 1.25,
  py: 0.9,
  mb: 0.45,
  alignItems: "stretch",
  border: "1px solid transparent",
  transition: "all 120ms ease",
  "&:last-of-type": {
    mb: 0,
  },
  "&:hover": {
    backgroundColor: "rgba(1, 36, 66, 0.06)",
    borderColor: "rgba(1, 36, 66, 0.12)",
  },
  '&[aria-selected="true"]': {
    backgroundColor: "rgba(1, 36, 66, 0.10)",
    borderColor: "rgba(1, 36, 66, 0.26)",
  },
  '&[aria-selected="true"] .route-option-primary': {
    color: "#001f3f",
  },
};

const routeOptionPrimarySx = {
  color: "#0f172a",
  fontSize: "0.92rem",
  fontWeight: 600,
  maxWidth: 330,
  lineHeight: 1.25,
};

const routeOptionSecondarySx = {
  color: "rgba(15, 23, 42, 0.78)",
  fontSize: "0.82rem",
  fontWeight: 500,
  lineHeight: 1.2,
  maxWidth: 440,
};

const separatorOptionSx = {
  listStyle: "none",
  py: 0.375,
  px: 1.25,
  minHeight: 0,
  cursor: "default",
  pointerEvents: "none",
  opacity: 1,
  backgroundColor: "transparent",
  "&.Mui-focused": { backgroundColor: "transparent" },
};

const separatorLineSx = {
  height: "1px",
  width: "100%",
  bgcolor: "divider",
  opacity: 0.35,
};

export default FreightDriverNegotiationCard;
